/**
 * DIコンテナ
 * すべての依存注入をここで管理
 */

import type { LearningProgressRepository } from '@/domain/precon-supporter/learning-progress';
import type { LearnerProfileRepository } from '@/domain/precon-supporter/learner-profile';
import type {
  ICertificateService,
  IExaminationService,
  IPasserService,
} from '@/domain/precon-supporter/services';

// Infrastructure層の実装
import {
  IndexedDBLearningProgressRepository,
  CertificateService,
  ExaminationService,
  PreconSupporterApiClient,
} from '@/infrastructure/precon-supporter';
import { PasserService } from '@/infrastructure/precon-supporter/services/passer';
import { IndexedDBLearnerProfileRepository } from '@/infrastructure/precon-supporter/persistence/indexeddb/IndexedDBLearnerProfileRepository';
import { indexedDBClient } from '@/infrastructure/precon-supporter/persistence/indexeddb/IndexedDBClient';

// Application層のユースケース
import {
  CompleteLectureUseCase,
  GetLearningProgressUseCase,
  ResetExaminationUseCase,
} from '@/application/precon-supporter';
import {
  GetLearnerProfileUseCase,
  SetWantsAdvancedCourseUseCase,
  SetNicknameUseCase,
} from '@/application/precon-supporter/usecases/learner-profile';
import { CompleteExaminationUseCase } from '@/application/precon-supporter/usecases/examination/CompleteExaminationUseCase';

/**
 * サービスコンテナ
 */
export class ServiceContainer {
  // リポジトリ
  readonly learningProgressRepository: LearningProgressRepository;
  readonly learnerProfileRepository: LearnerProfileRepository;

  // サービス
  readonly certificateService: ICertificateService;
  readonly examinationService: IExaminationService;
  readonly passerService: IPasserService;

  // ユースケース
  readonly completeLectureUseCase: CompleteLectureUseCase;
  readonly getLearningProgressUseCase: GetLearningProgressUseCase;
  readonly getLearnerProfileUseCase: GetLearnerProfileUseCase;
  readonly setWantsAdvancedCourseUseCase: SetWantsAdvancedCourseUseCase;
  readonly setNicknameUseCase: SetNicknameUseCase;
  readonly completeExaminationUseCase: CompleteExaminationUseCase;
  readonly resetExaminationUseCase: ResetExaminationUseCase;

  constructor() {
    // リポジトリのインスタンス化
    this.learningProgressRepository = new IndexedDBLearningProgressRepository();
    this.learnerProfileRepository = new IndexedDBLearnerProfileRepository(
      indexedDBClient
    );

    // サービスのインスタンス化
    this.certificateService = new CertificateService();

    // API関連サービスのインスタンス化
    const preconSupporterApiClient = new PreconSupporterApiClient();
    this.examinationService = new ExaminationService(preconSupporterApiClient);
    this.passerService = new PasserService(preconSupporterApiClient);

    // ユースケースのインスタンス化（依存を注入）
    this.completeLectureUseCase = new CompleteLectureUseCase(
      this.learningProgressRepository
    );

    this.getLearningProgressUseCase = new GetLearningProgressUseCase(
      this.learningProgressRepository
    );

    // LearnerProfile用ユースケース
    this.getLearnerProfileUseCase = new GetLearnerProfileUseCase(
      this.learnerProfileRepository
    );

    this.setWantsAdvancedCourseUseCase = new SetWantsAdvancedCourseUseCase(
      this.learnerProfileRepository
    );

    this.setNicknameUseCase = new SetNicknameUseCase(
      this.learnerProfileRepository
    );

    this.completeExaminationUseCase = new CompleteExaminationUseCase(
      this.learningProgressRepository
    );

    this.resetExaminationUseCase = new ResetExaminationUseCase(
      this.learningProgressRepository
    );
  }
}

// シングルトンインスタンス（SSR時は遅延初期化）
let _container: ServiceContainer | null = null;

export const container: ServiceContainer = new Proxy({} as ServiceContainer, {
  get(_target, prop) {
    if (typeof window === 'undefined') {
      // SSR時はダミーを返す
      return undefined;
    }
    if (!_container) {
      _container = new ServiceContainer();
    }
    return _container[prop as keyof ServiceContainer];
  },
});
